// src/shared/hooks/useInfiniteScroll.js
import { useEffect, useRef, useState, useCallback } from 'react';

export const useInfiniteScroll = ({
    onLoadMore,
    hasMore,
    isLoading,
    threshold = 0.1,
    rootMargin = '100px'
}) => {
    const [sentinel, setSentinel] = useState(null);
    const observerRef = useRef(null);

    // Callback ref so the effect re-runs when the sentinel element mounts/unmounts
    const sentinelRef = useCallback((node) => {
        setSentinel(node);
    }, []);

    const handleIntersect = useCallback((entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !isLoading) {
            onLoadMore();
        }
    }, [onLoadMore, hasMore, isLoading]);

    useEffect(() => {
        if (!sentinel) return;

        if (observerRef.current) {
            observerRef.current.disconnect();
        }

        observerRef.current = new IntersectionObserver(handleIntersect, {
            root: null,
            rootMargin,
            threshold
        });

        observerRef.current.observe(sentinel);

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [sentinel, handleIntersect, rootMargin, threshold]);

    return { sentinelRef };
};

export default useInfiniteScroll;
